import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Loader from '@/components/common/Loader';
import { ForbiddenCard } from '@/components/common/ForbiddenCard';
import { useMyOgnization } from '@/api-client/use-my-ognization';
import { useGetOrganizationsByBlocksOrgIds } from '@/graphql/queries/organizations';
import { useGetOrgContractDocumentById } from '@/graphql';
import {
  GUEST_ORG_SENTINEL,
  isContractEditable,
} from '@/modules/org/contracts/types/contract-document.types';
import { isFixedDocxContract } from '@/modules/org/contracts/utils';
import DocxEditableBranch from '@/modules/templates/components/docx-generate/docx-review/DocxEditableBranch';
import DocxLockedReview from '@/modules/templates/components/docx-generate/docx-review/DocxLockedReview';

import TemplatePDFReview from './TemplatePDFReview';
import { ReviewMessage } from './ReviewMessage';
import { ReviewUnavailableCard } from './ReviewUnavailableCard';
import { resolveReviewData, type ReviewData } from './review-data';
import type { OrgContractDocumentItem } from '@/modules/org/contracts/types/contract-document.types';
import { useAuthStore } from '@/state/store/auth';

export const canAccessApprovalReview = (
  contract: Pick<OrgContractDocumentItem, 'HasSentForApproval' | 'Reviewers'> | null | undefined,
  userEmail: string | null | undefined
): boolean => {
  if (contract?.HasSentForApproval !== true || !userEmail) return false;

  const normalizedUserEmail = userEmail.trim().toLowerCase();
  return (
    normalizedUserEmail.length > 0 &&
    (contract.Reviewers ?? []).some(
      (reviewerEmail) => reviewerEmail.trim().toLowerCase() === normalizedUserEmail
    )
  );
};

/**
 * Which renderer a contract needs, carrying whatever that renderer requires.
 * Modelled as a union so each branch arrives already narrowed.
 */
type ReviewTarget =
  | { kind: 'docx-editable'; contract: OrgContractDocumentItem }
  | { kind: 'docx-locked'; contract: OrgContractDocumentItem }
  | { kind: 'legal-document'; data: ReviewData }
  | { kind: 'forbidden' }
  | { kind: 'not-found' }
  | { kind: 'unavailable' };

interface ReviewContentProps {
  hideDocumentActions?: boolean;
}

/**
 * The review route, `/templates/:slug/review`.
 *
 * Loads the contract once and decides — in one place — which review surface it
 * needs. Two document models live here: fixed-DOCX contracts store a `.docx`
 * reference, everything else stores a `LegalDocument`. Either is editable until
 * it's sent for signature, after which it becomes read-only.
 */
const ReviewContent = ({ hideDocumentActions = false }: ReviewContentProps) => {
  const [searchParams] = useSearchParams();
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userEmail = useAuthStore((state) => state.user?.email);
  const { data: myOrgs, isLoading: isIamOrgsLoading } = useMyOgnization();
  const blocksOrgIds = useMemo(
    () => myOrgs?.organizations?.map((org) => org.itemId) ?? [],
    [myOrgs]
  );
  const { data: udsOrgs, isLoading: isUdsOrgsLoading } = useGetOrganizationsByBlocksOrgIds(
    blocksOrgIds,
    { requireSignatureLink: false, enabled: blocksOrgIds.length > 0 }
  );

  // `?document=<itemId>` names the OrgContractDocument to load. Without it we
  // fall back to whatever the generate step left in session storage.
  //
  // `?draft` is the legacy spelling and is still read by `TemplatePDFReview`.
  // Accept it here too, so both components resolve the same row — otherwise a
  // `?draft`-only URL skips this component's authorization check and the child
  // loads the contract on its own.
  const documentId = searchParams.get('document') ?? searchParams.get('draft') ?? '';

  const {
    data: contract,
    isLoading: isFetching,
    error: fetchError,
  } = useGetOrgContractDocumentById(documentId, !!documentId);

  // Contracts owned by an organization are readable only by its members. This
  // route sits outside `/org/:slug`, so nothing upstream has proved membership.
  const owner = contract?.OrganizationId;
  const access = useMemo(() => {
    if (!contract || !owner || owner === GUEST_ORG_SENTINEL) return 'allowed' as const;
    if (isIamOrgsLoading || (blocksOrgIds.length > 0 && isUdsOrgsLoading)) {
      return 'checking' as const;
    }
    const udsOrgIds = new Set((udsOrgs ?? []).map((org) => org.ItemId));
    return udsOrgIds.has(owner) ? ('allowed' as const) : ('forbidden' as const);
  }, [blocksOrgIds.length, contract, isIamOrgsLoading, isUdsOrgsLoading, owner, udsOrgs]);
  const hasApprovalAccess = canAccessApprovalReview(contract, userEmail);

  const reviewData = useMemo(
    () => resolveReviewData({ contract, documentId, slug }),
    [contract, documentId, slug]
  );

  const target = useMemo<ReviewTarget>(() => {
    // Before anything else — a contract the visitor may not see must never
    // reach a renderer, whichever document model it uses.
    if (hideDocumentActions && (!documentId || !contract || !hasApprovalAccess)) {
      return { kind: 'forbidden' };
    }
    if (!hideDocumentActions && access === 'forbidden') return { kind: 'forbidden' };
    if (documentId && !contract) return { kind: 'not-found' };
    if (contract && isFixedDocxContract(contract.Document)) {
      return isContractEditable(contract)
        ? { kind: 'docx-editable', contract }
        : { kind: 'docx-locked', contract };
    }
    return reviewData ? { kind: 'legal-document', data: reviewData } : { kind: 'unavailable' };
  }, [access, contract, documentId, hasApprovalAccess, hideDocumentActions, reviewData]);

  const goToGenerate = () => navigate(-1);
  const goBack = () => navigate(-1);

  if ((documentId && isFetching) || (!hideDocumentActions && access === 'checking')) {
    return <Loader />;
  }

  if (fetchError) {
    return <ReviewUnavailableCard reason="load-failed" onBack={goBack} />;
  }

  switch (target.kind) {
    case 'docx-editable':
      return (
        <DocxEditableBranch
          contract={target.contract}
          documentId={documentId}
          slug={slug}
          hideDocumentActions={hideDocumentActions}
        />
      );

    case 'docx-locked':
      return <DocxLockedReview contract={target.contract} documentId={documentId} slug={slug} />;

    case 'legal-document':
      return (
        <TemplatePDFReview
          document={target.data.document}
          formData={target.data.formData}
          templateSlug={target.data.templateSlug}
          templateTitle={target.data.templateTitle}
          onBack={goToGenerate}
          hideDocumentActions={hideDocumentActions}
        />
      );

    case 'forbidden':
      return (
        <ForbiddenCard
          description={
            hideDocumentActions
              ? t('GENERATEREVIEW_APPROVAL_ACCESS_FORBIDDEN', {
                defaultValue:
                  'This approval link is only available to the reviewer invited for this document.',
              })
              : t('FORBIDDEN_CONTRACT_DESCRIPTION', {
                defaultValue:
                  "This document belongs to an organization you're not a member of. Ask an admin of that organization to add you, or switch to an account that has access.",
              })
          }
          onBack={goBack}
        />
      );

    case 'not-found':
      return <ReviewMessage>We couldn’t find this document.</ReviewMessage>;

    case 'unavailable':
    default:
      // The record loaded but holds nothing reviewable — typically a draft
      // abandoned before the generate step finished.
      return (
        <ReviewUnavailableCard
          reason="not-generated"
          onBack={goBack}
          onGoToGenerate={slug ? goToGenerate : undefined}
        />
      );
  }
};

export default ReviewContent;
